import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { callClaude } from '../utils/claudeProxy';
import {
  collection, addDoc, getDocs, doc, updateDoc,
  deleteDoc, getDoc
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
  Box, Container, Typography, Button, Paper, TextField,
  IconButton, Alert, CircularProgress, Chip, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Grid, List, ListItem, ListItemText,
  ListItemSecondaryAction, Collapse, Stepper, Step, StepLabel,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PreviewIcon from '@mui/icons-material/Preview';
import ImageUpload from '../components/ImageUpload';
import { useNavigate } from 'react-router-dom';
import SoftwareTagInput from '../components/SoftwareTagInput';
import { useSoftwareTags } from '../hooks/useSoftwareTags';

const EMPTY_FORM = {
  projectNumber: '', name: '', role: '', consultantName: '',
  responsible: '', client: '', clientOrgNumber: '', budget: '',
  startDate: '', endDate: '', contactName: '', contactRole: '',
  contactEmail: '', contactPhone: '', projectDescription: '',
  ourAssignment: '', taskList: [], keywords: [], standards: [],
  software: [], projectType: '', image: '', departmentId: '',
};

const LABEL_WIDTH = 110;

const DETAIL_PROMPTS = {
  short: `Var kortfattad – max 2-3 meningar per beskrivningsfält. Uppgiftslistan max 5 punkter.`,
  medium: `Var lagom detaljerad – 3-5 meningar per beskrivningsfält. Uppgiftslistan max 10 punkter.`,
  long: `Var utforlig och inkludera all information. Alla detaljer och hela uppgiftslistan.`
};

// Hämta befintliga taggar för normalisering
async function fetchExistingTags() {
  try {
    const [profileSnap, refSnap, softwareSnap] = await Promise.all([
      getDocs(collection(db, 'profiles')),
      getDocs(collection(db, 'referenceProjects')),
      getDocs(collection(db, 'softwareTags')),
    ]);
    const keywords = new Set();
    const standards = new Set();
    const software = new Set();
    refSnap.docs.forEach(d => {
      const data = d.data();
      (data.keywords || []).forEach(t => keywords.add(t));
      (data.standards || []).forEach(t => standards.add(t));
      (data.software || []).forEach(t => software.add(t));
    });
    profileSnap.docs.forEach(d => {
      const data = d.data();
      (data.standards || []).forEach(t => standards.add(t));
    });
    softwareSnap.docs.forEach(d => {
      const name = d.data().name;
      if (name) software.add(name);
    });
    return {
      keywords: [...keywords],
      standards: [...standards],
      software: [...software],
    };
  } catch {
    return { keywords: [], standards: [], software: [] };
  }
}

function buildTagInstruction(tags) {
  return `
VIKTIG INSTRUKTION FÖR TAGGAR (nyckelord, standarder och programvaror):
Använd EXAKT samma stavning och skiftlage som i dessa befintliga taggar om de passar:
- Nyckelord: ${tags.keywords.slice(0, 50).join(', ') || 'inga ännu'}
- Standarder: ${tags.standards.slice(0, 30).join(', ') || 'inga ännu'}
- Programvaror: ${tags.software.slice(0, 50).join(', ') || 'inga ännu'}

Skapa bara NYA taggar om inget befintligt alternativ passar.
Normalisera alltid: behåll branschspecifika förkortningar i versaler
(ISO, BIM, CAD, IFC, ABK, AMA etc). Programvarunamn skrivs med officiell stavning
(t.ex. AutoCAD, Revit, Bluebeam, NovaPOINT, Trimble).`;
}

function buildAIPrompt(detailLevel, tags) {
  const tagInstruction = tags ? buildTagInstruction(tags) : '';
  return `Analysera detta referensuppdragsdokument från Tikab och extrahera all information.
Dokumentet har typiskt fyra sektioner:
01 - Projektbild
02 - Referensperson hos beställare (tabell)
03 - Uppgifter kring uppdrag (tabell)
04 - Beskrivning av uppdrag (fritext + punktlista)

DETALJNIVA: ${DETAIL_PROMPTS[detailLevel]}

${tagInstruction}

Returnera ENDAST ett JSON-objekt:
{
  "projectNumber": "uppdragsnummer",
  "name": "uppdragsnamn/projektnamn",
  "role": "roll i uppdraget",
  "consultantName": "konsultens namn",
  "responsible": "uppdragsansvarig",
  "client": "uppdragsgivare",
  "clientOrgNumber": "organisationsnummer",
  "budget": "projektbudget",
  "startDate": "uppdragstart",
  "endDate": "uppdragslut",
  "contactName": "referensperson namn",
  "contactRole": "referensperson befattning",
  "contactEmail": "referensperson e-post",
  "contactPhone": "referensperson telefon",
  "projectDescription": "Beskrivning av projektet i stort",
  "ourAssignment": "Beskrivning av Tikabs specifika uppdrag ansvar och roll",
  "taskList": ["konkret uppgift 1", "konkret uppgift 2"],
  "keywords": ["nyckelord 1", "nyckelord 2"],
  "standards": ["standard 1"],
  "software": ["programvara 1", "programvara 2"],
  "projectType": "typ av projekt"
}`;
// software: programvaror, digitala verktyg och IT-system som användes i uppdraget
}

export default function ReferenceProjects() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [focusId, setFocusId] = useState(null); // fokuserat uppdrag (alla andra minimeras)
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [sortBy, setSortBy] = useState('number'); // 'number' | 'name' | 'client'
  const [detailLevel, setDetailLevel] = useState('medium');
  const [dialog, setDialog] = useState({ open: false, editId: '' });
  const [dialogStep, setDialogStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [tempProjectId, setTempProjectId] = useState(null);
  const [taskInput, setTaskInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [standardInput, setStandardInput] = useState('');

  const { pool: softwarePool, addToPool: addSoftware } = useSoftwareTags();

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    setLoading(true);
    const [projSnap, deptSnap, userSnap] = await Promise.all([
      getDocs(collection(db, 'referenceProjects')),
      getDocs(collection(db, 'departments')),
      getDoc(doc(db, 'users', currentUser.uid)),
    ]);
    const allProjects = projSnap.docs.map(d => ({
      id: d.id, ...d.data()
    }));
    const userData = userSnap.exists() ? userSnap.data() : {};
    let filtered = allProjects;
    if (userData.role === 'medarbetare' || userData.isAnbudsansvarig) {
      filtered = allProjects.filter(
        p => p.departmentId === userData.departmentId);
    } else if (userData.isAdmin) {
      filtered = allProjects.filter(
        p => p.companyId === userData.companyId);
    }
    setProjects(filtered);
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  function toggleCollapse(id) {
    if (focusId) return; // i fokusläge styr fokus-knappen
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleFocus(id) {
    if (focusId === id) {
      setFocusId(null); // lämna fokusläge
    } else {
      setFocusId(id); // fokusera detta, minimera alla andra
    }
  }

  function openNew() {
    setForm(EMPTY_FORM);
    setTaskInput(''); setKeywordInput(''); setStandardInput('');
    setPendingFiles([]);
    setTempProjectId(null);
    setDialogStep(0);
    setDialog({ open: true, editId: '' });
  }

  function openEdit(project) {
    setForm({
      projectNumber: project.projectNumber || '',
      name: project.name || '',
      role: project.role || '',
      consultantName: project.consultantName || '',
      responsible: project.responsible || '',
      client: project.client || '',
      clientOrgNumber: project.clientOrgNumber || '',
      budget: project.budget || '',
      startDate: project.startDate || '',
      endDate: project.endDate || '',
      contactName: project.contactName || '',
      contactRole: project.contactRole || '',
      contactEmail: project.contactEmail || '',
      contactPhone: project.contactPhone || '',
      projectDescription: project.projectDescription || '',
      ourAssignment: project.ourAssignment || '',
      taskList: project.taskList || [],
      keywords: project.keywords || [],
      standards: project.standards || [],
      software: project.software || [],
      projectType: project.projectType || '',
      image: project.image || '',
      departmentId: project.departmentId || '',
    });
    setPendingFiles([]);
    setTempProjectId(project.id);
    setDialogStep(1);
    setDialog({ open: true, editId: project.id });
  }

  async function runAIAnalysis(projectId, files, level) {
    // Hämta befintliga taggar för normalisering
    const tags = await fetchExistingTags();

    const fileDocs = await Promise.all(files.map(async file => {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      return { name: file.name, base64 };
    }));

    const content = [
      ...fileDocs.map(f => ({
        type: 'document',
        source: {
          type: 'base64',
          media_type: 'application/pdf',
          data: f.base64
        },
        title: f.name
      })),
      { type: 'text', text: buildAIPrompt(level, tags) }
    ];

    const data = await callClaude({
      model: 'claude-opus-5',
      max_tokens: 6000,
      messages: [{ role: 'user', content }]
    });
    const raw = data.content[0].text;
    const clean = raw.replace(/```json|```/g, '').trim();
    return JSON.parse(clean);
  }

  async function handleUploadAndAnalyze() {
    if (pendingFiles.length === 0) {
      setDialogStep(1);
      return;
    }
    setImporting(true);
    setError('');
    try {
      const dept = departments.find(d => d.id === form.departmentId);
      const docRef = await addDoc(
        collection(db, 'referenceProjects'), {
        ...EMPTY_FORM,
        name: 'Importeras...',
        companyId: dept?.companyId || '',
        sourceFiles: [],
        createdBy: currentUser.uid,
        createdAt: new Date()
      });
      const projectId = docRef.id;
      setTempProjectId(projectId);

      const uploadedFiles = [];
      for (const file of pendingFiles) {
        const fileKey = 'new_' + file.name;
        setUploadProgress(prev => ({ ...prev, [fileKey]: 1 }));
        const storageRef = ref(storage,
          'referenceProjects/' + projectId + '/source/' +
          Date.now() + '_' + file.name);
        const uploadTask = uploadBytesResumable(storageRef, file);
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            snap => {
              const pct = Math.round(
                snap.bytesTransferred / snap.totalBytes * 100);
              setUploadProgress(prev => ({
                ...prev, [fileKey]: pct
              }));
            },
            err => { reject(err); },
            async () => {
              const url = await getDownloadURL(
                uploadTask.snapshot.ref);
              uploadedFiles.push({
                name: file.name, url,
                uploadedAt: new Date().toISOString()
              });
              setUploadProgress(prev => {
                const n = { ...prev };
                delete n[fileKey];
                return n;
              });
              resolve();
            }
          );
        });
      }

      await updateDoc(doc(db, 'referenceProjects', projectId), {
        sourceFiles: uploadedFiles
      });

      const parsed = await runAIAnalysis(
        projectId, uploadedFiles, detailLevel);

      setForm(prev => ({
        ...prev,
        projectNumber: parsed.projectNumber || '',
        name: parsed.name || '',
        role: parsed.role || '',
        consultantName: parsed.consultantName || '',
        responsible: parsed.responsible || '',
        client: parsed.client || '',
        clientOrgNumber: parsed.clientOrgNumber || '',
        budget: parsed.budget || '',
        startDate: parsed.startDate || '',
        endDate: parsed.endDate || '',
        contactName: parsed.contactName || '',
        contactRole: parsed.contactRole || '',
        contactEmail: parsed.contactEmail || '',
        contactPhone: parsed.contactPhone || '',
        projectDescription: parsed.projectDescription || '',
        ourAssignment: parsed.ourAssignment || '',
        taskList: parsed.taskList || [],
        keywords: parsed.keywords || [],
        standards: parsed.standards || [],
        software: parsed.software || [],
        projectType: parsed.projectType || '',
      }));

      await updateDoc(doc(db, 'referenceProjects', projectId), {
        ...parsed,
        sourceFiles: uploadedFiles,
        aiImportedAt: new Date(),
        detailLevel
      });

      setDialogStep(1);
    } catch (err) {
      console.error(err);
      setError('Kunde inte analysera dokumentet. Försök igen.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setImporting(false);
    }
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    const dept = departments.find(d => d.id === form.departmentId);
    const data = {
      ...form,
      companyId: dept?.companyId || '',
      updatedAt: new Date()
    };
    if (tempProjectId && !dialog.editId) {
      await updateDoc(
        doc(db, 'referenceProjects', tempProjectId), data);
    } else if (dialog.editId) {
      await updateDoc(
        doc(db, 'referenceProjects', dialog.editId), data);
    } else {
      await addDoc(collection(db, 'referenceProjects'), {
        ...data, sourceFiles: [],
        createdBy: currentUser.uid,
        createdAt: new Date()
      });
    }
    setDialog({ open: false, editId: '' });
    setSaved('Referensuppdrag sparat!');
    setTimeout(() => setSaved(''), 2000);
    loadAll();
  }

  async function handleDelete(id) {
    await deleteDoc(doc(db, 'referenceProjects', id));
    loadAll();
  }

  async function handleUploadSourceFile(e, projectId) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    for (const file of files) {
      const fileKey = projectId + '_' + file.name;
      setUploadProgress(prev => ({ ...prev, [fileKey]: 1 }));
      const storageRef = ref(storage,
        'referenceProjects/' + projectId + '/source/' +
        Date.now() + '_' + file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          snap => {
            const pct = Math.round(
              snap.bytesTransferred / snap.totalBytes * 100);
            setUploadProgress(prev => ({
              ...prev, [fileKey]: pct
            }));
          },
          err => { reject(err); },
          async () => {
            const url = await getDownloadURL(
              uploadTask.snapshot.ref);
            const snap = await getDoc(
              doc(db, 'referenceProjects', projectId));
            const existing = snap.data()?.sourceFiles || [];
            await updateDoc(
              doc(db, 'referenceProjects', projectId), {
              sourceFiles: [...existing, {
                name: file.name, url,
                uploadedAt: new Date().toISOString()
              }]
            });
            setUploadProgress(prev => {
              const n = { ...prev };
              delete n[fileKey];
              return n;
            });
            setProjects(prev => prev.map(p =>
              p.id === projectId
                ? { ...p, sourceFiles: [
                    ...(p.sourceFiles || []),
                    { name: file.name, url }
                  ]}
                : p
            ));
            resolve();
          }
        );
      });
    }
    setSaved('Fil uppladdad!');
    setTimeout(() => setSaved(''), 2000);
  }

  async function handleDeleteSourceFile(projectId, fileUrl) {
    const snap = await getDoc(
      doc(db, 'referenceProjects', projectId));
    const existing = snap.data()?.sourceFiles || [];
    const newFiles = existing.filter(f => f.url !== fileUrl);
    await updateDoc(doc(db, 'referenceProjects', projectId), {
      sourceFiles: newFiles
    });
    setProjects(prev => prev.map(p =>
      p.id === projectId
        ? { ...p, sourceFiles: newFiles }
        : p
    ));
  }

  async function handleAIImport(projectId, level) {
    setImporting(true);
    setImportingId(projectId);
    setError('');
    try {
      const snap = await getDoc(
        doc(db, 'referenceProjects', projectId));
      const projectData = snap.data();
      const files = projectData?.sourceFiles || [];
      if (!files.length) {
        setError('Ladda upp ett dokument först.');
        setImporting(false);
        setImportingId(null);
        return;
      }
      const parsed = await runAIAnalysis(
        projectId, files, level || detailLevel);
      const updates = {
        ...parsed,
        aiImportedAt: new Date(),
        detailLevel: level || detailLevel
      };
      await updateDoc(
        doc(db, 'referenceProjects', projectId), updates);
      setProjects(prev => prev.map(p =>
        p.id === projectId ? { ...p, ...updates } : p
      ));
      setSaved('AI-import klar!');
      setTimeout(() => setSaved(''), 3000);
    } catch (err) {
      console.error(err);
      setError('Kunde inte importera.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setImporting(false);
      setImportingId(null);
    }
  }

  function handleCloseDialog() {
    if (tempProjectId && !dialog.editId) {
      deleteDoc(doc(db, 'referenceProjects', tempProjectId));
    }
    setDialog({ open: false, editId: '' });
    setTempProjectId(null);
    setPendingFiles([]);
    setDialogStep(0);
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white',
        py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LagbasLogo />
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
            <IconButton color="inherit"
              onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              Referensuppdrag
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: 'white', color: '#1a1a1a' }}
            onClick={openNew}>
            Nytt referensuppdrag
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saved && (
          <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {importing && !importingId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            AI analyserar dokumentet – detta kan ta en stund...
          </Alert>
        )}

        {Object.keys(uploadProgress).length > 0 && (
          <Box sx={{ mb: 2 }}>
            {Object.entries(uploadProgress).map(([key, pct]) => (
              <Box key={key} sx={{ mb: 1 }}>
                <Typography variant="caption">
                  {key.split('_').slice(1).join('_')}
                </Typography>
                <LinearProgress variant="determinate" value={pct} />
              </Box>
            ))}
          </Box>
        )}

        {/* Filter / sök / sortera */}
        {projects.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Sök namn, nummer, kund…"
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              sx={{ minWidth: 200, flex: '1 1 200px',
                '& .MuiInputBase-input': { fontSize: '0.82rem' } }}
            />
            <TextField select size="small" label="Affärsområde"
              value={filterDept}
              onChange={e => setFilterDept(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: 160, '& .MuiInputBase-input': { fontSize: '0.82rem' } }}>
              <option value="">Alla</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </TextField>
            <TextField select size="small" label="Sortera"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ minWidth: 150, '& .MuiInputBase-input': { fontSize: '0.82rem' } }}>
              <option value="number">Uppdragsnummer</option>
              <option value="name">Namn A–Ö</option>
              <option value="client">Kund A–Ö</option>
            </TextField>
            {(filterSearch || filterDept) && (
              <Button size="small" variant="text" onClick={() => { setFilterSearch(''); setFilterDept(''); }}
                sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Rensa
              </Button>
            )}
            {focusId && (
              <Chip size="small" label="Fokusläge aktivt"
                onDelete={() => setFocusId(null)}
                sx={{ bgcolor: '#FF6B00', color: '#fff',
                  '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }} />
            )}
          </Box>
        )}

        {projects.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <AutoFixHighIcon sx={{ fontSize: 48, color: '#FF6B00',
              mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Inga referensuppdrag ännu
            </Typography>
            <Typography variant="body2" color="text.secondary"
              gutterBottom>
              Ladda upp ert referensblad (PDF) –
              AI fyller i alla fält automatiskt!
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />}
              sx={{ mt: 2, bgcolor: '#FF6B00', color: 'white',
                '&:hover': { bgcolor: '#e65c00' } }}
              onClick={openNew}>
              Skapa första referensuppdraget
            </Button>
          </Paper>
        )}

        {(() => {
          const q = filterSearch.toLowerCase();
          const filtered = projects
            .filter(p => {
              if (filterDept && p.departmentId !== filterDept) return false;
              if (q && !(
                (p.name || '').toLowerCase().includes(q) ||
                (p.projectNumber || '').toLowerCase().includes(q) ||
                (p.client || '').toLowerCase().includes(q)
              )) return false;
              return true;
            })
            .sort((a, b) => {
              if (sortBy === 'number') return (a.projectNumber || '').localeCompare(b.projectNumber || '', 'sv', { numeric: true });
              if (sortBy === 'name')   return (a.name || '').localeCompare(b.name || '', 'sv');
              if (sortBy === 'client') return (a.client || '').localeCompare(b.client || '', 'sv');
              return 0;
            });

          if (filtered.length === 0 && projects.length > 0) return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Inga uppdrag matchar filtret.
            </Typography>
          );

          return filtered.map(project => {
          const isCollapsed = focusId ? focusId !== project.id : !!collapsed[project.id];
          const isImporting = importing &&
            importingId === project.id;

          return (
            <Paper key={project.id} sx={{ mb: 2, overflow: 'hidden' }}>

              {/* HEADER */}
              <Box sx={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', p: 2,
                borderBottom: isCollapsed ? 'none' : '1px solid',
                borderColor: 'divider',
              }}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  flex: 1, minWidth: 0, cursor: 'pointer'
                }}
                  onClick={() => toggleCollapse(project.id)}>
                  {project.image ? (
                    <Box component="img" src={project.image}
                      sx={{ width: 56, height: 42,
                        objectFit: 'cover', borderRadius: 1,
                        flexShrink: 0, border: '1px solid',
                        borderColor: 'divider' }} />
                  ) : (
                    <Box sx={{ width: 56, height: 42,
                      borderRadius: 1, flexShrink: 0,
                      bgcolor: 'action.hover',
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px dashed', borderColor: 'divider' }}>
                      <Typography sx={{ fontSize: '1.2rem' }}>
                        🏗️
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex',
                      alignItems: 'center', gap: 1.5,
                      flexWrap: 'wrap' }}>
                      {project.projectNumber && (
                        <Chip label={'#' + project.projectNumber}
                          size="small"
                          sx={{ bgcolor: '#1a1a1a', color: 'white',
                            fontSize: '0.65rem', height: 20 }} />
                      )}
                      <Typography variant="subtitle1"
                        fontWeight="bold">
                        {project.name}
                      </Typography>
                      {project.role && (
                        <Chip label={project.role} size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }} />
                      )}
                      {project.client && (
                        <Typography variant="body2"
                          color="text.secondary">
                          🏢 {project.client}
                        </Typography>
                      )}
                      {project.budget && (
                        <Typography variant="body2"
                          color="text.secondary">
                          💰 {project.budget}
                        </Typography>
                      )}
                      {project.aiImportedAt && (
                        <Chip
                          label={'AI-' + (
                            project.detailLevel === 'short' ? 'S'
                            : project.detailLevel === 'long' ? 'L'
                            : 'M')}
                          size="small"
                          sx={{ bgcolor: '#fff8e1',
                            color: '#f57f17',
                            fontSize: '0.65rem', height: 20 }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.3 }}>
                      {project.consultantName && (
                        <Typography variant="caption"
                          color="text.secondary">
                          👤 {project.consultantName}
                        </Typography>
                      )}
                      {(project.startDate || project.endDate) && (
                        <Typography variant="caption"
                          color="text.secondary">
                          📅 {project.startDate}
                          {project.endDate
                            ? ' – ' + project.endDate : ''}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <IconButton size="small" sx={{ flexShrink: 0 }}>
                    {isCollapsed
                      ? <ExpandMoreIcon />
                      : <ExpandLessIcon />}
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center',
                  gap: 1, ml: 1, flexShrink: 0 }}>
                  <Button size="small"
                    variant={focusId === project.id ? 'contained' : 'outlined'}
                    onClick={() => toggleFocus(project.id)}
                    sx={focusId === project.id
                      ? { bgcolor: '#FF6B00', color: '#fff', '&:hover': { bgcolor: '#e65c00' }, fontSize: '0.7rem' }
                      : { fontSize: '0.7rem' }}>
                    {focusId === project.id ? 'Avsluta fokus' : 'Fokusera'}
                  </Button>
                  <IconButton size="small" title="Förhandsgranska"
                    onClick={() => navigate(
                      '/reference-projects/' +
                      project.id + '/preview')}>
                    <PreviewIcon fontSize="small" />
                  </IconButton>
                  <Button size="small" variant="outlined"
                    component="label" startIcon={<UploadFileIcon />}>
                    Ladda upp PDF
                    <input type="file" hidden accept=".pdf,.docx"
                      multiple
                      onChange={e =>
                        handleUploadSourceFile(e, project.id)} />
                  </Button>
                  {project.sourceFiles?.length > 0 && (
                    <Button size="small" variant="outlined"
                      startIcon={isImporting
                        ? <CircularProgress size={14} />
                        : <AutoFixHighIcon />}
                      onClick={() =>
                        handleAIImport(project.id, detailLevel)}
                      disabled={importing}
                      sx={{ borderColor: '#FF6B00',
                        color: '#FF6B00' }}>
                      {isImporting ? 'Importerar...' : 'AI-import'}
                    </Button>
                  )}
                  <IconButton size="small"
                    onClick={() => openEdit(project)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error"
                    onClick={() => handleDelete(project.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* INNEHÅLL */}
              <Collapse in={!isCollapsed}>
                <Box sx={{ p: 3 }}>
                  <Grid container spacing={2}>

                    {/* VÄNSTER */}
                    <Grid item xs={12} md={project.image ? 8 : 12}>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <Paper variant="outlined"
                            sx={{ p: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex',
                              alignItems: 'center',
                              gap: 1, mb: 1.5 }}>
                              <PersonIcon sx={{ fontSize: 16,
                                color: '#FF6B00' }} />
                              <Typography variant="caption"
                                fontWeight="bold"
                                color="text.secondary"
                                sx={{ letterSpacing: 1 }}>
                                REFERENSPERSON HOS BESTÄLLARE
                              </Typography>
                            </Box>
                            {[
                              { label: 'Uppdragsgivare',
                                value: project.client },
                              { label: 'Org.nr',
                                value: project.clientOrgNumber },
                              { label: 'Namn',
                                value: project.contactName },
                              { label: 'Befattning',
                                value: project.contactRole },
                              { label: 'E-post',
                                value: project.contactEmail },
                              { label: 'Telefon',
                                value: project.contactPhone },
                            ].filter(r => r.value).map((row, i) => (
                              <Box key={i} sx={{ display: 'flex',
                                gap: 1, mb: 0.5 }}>
                                <Typography variant="caption"
                                  color="text.secondary"
                                  sx={{ width: LABEL_WIDTH,
                                    flexShrink: 0 }}>
                                  {row.label}:
                                </Typography>
                                <Typography variant="caption"
                                  fontWeight="bold">
                                  {row.value}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Paper variant="outlined"
                            sx={{ p: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex',
                              alignItems: 'center',
                              gap: 1, mb: 1.5 }}>
                              <BusinessIcon sx={{ fontSize: 16,
                                color: '#FF6B00' }} />
                              <Typography variant="caption"
                                fontWeight="bold"
                                color="text.secondary"
                                sx={{ letterSpacing: 1 }}>
                                UPPGIFTER KRING UPPDRAG
                              </Typography>
                            </Box>
                            {[
                              { label: 'Projekt',
                                value: project.name },
                              { label: 'Budget',
                                value: project.budget },
                              { label: 'Start',
                                value: project.startDate },
                              { label: 'Slut',
                                value: project.endDate },
                              { label: 'Roll',
                                value: project.role },
                              { label: 'Konsult',
                                value: project.consultantName },
                              { label: 'Typ',
                                value: project.projectType },
                            ].filter(r => r.value).map((row, i) => (
                              <Box key={i} sx={{ display: 'flex',
                                gap: 1, mb: 0.5 }}>
                                <Typography variant="caption"
                                  color="text.secondary"
                                  sx={{ width: LABEL_WIDTH,
                                    flexShrink: 0 }}>
                                  {row.label}:
                                </Typography>
                                <Typography variant="caption"
                                  fontWeight="bold">
                                  {row.value}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        </Grid>
                      </Grid>

                      {project.projectDescription && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption"
                            fontWeight="bold" color="text.secondary"
                            sx={{ letterSpacing: 1 }}>
                            BESKRIVNING AV UPPDRAG
                          </Typography>
                          <Typography variant="body2"
                            sx={{ mt: 0.5, lineHeight: 1.7 }}>
                            {project.projectDescription}
                          </Typography>
                        </Box>
                      )}

                      {project.ourAssignment && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption"
                            fontWeight="bold" color="text.secondary"
                            sx={{ letterSpacing: 1 }}>
                            VART UPPDRAG
                          </Typography>
                          <Typography variant="body2"
                            sx={{ mt: 0.5, lineHeight: 1.7 }}>
                            {project.ourAssignment}
                          </Typography>
                        </Box>
                      )}

                      {project.taskList?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption"
                            fontWeight="bold" color="text.secondary"
                            sx={{ letterSpacing: 1 }}>
                            UPPGIFTER
                          </Typography>
                          {project.taskList.map((task, i) => (
                            <Typography key={i} variant="body2"
                              sx={{ mt: 0.3, pl: 1 }}>
                              • {task}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 3,
                        flexWrap: 'wrap', mb: 1 }}>
                        {project.keywords?.length > 0 && (
                          <Box>
                            <Typography variant="caption"
                              fontWeight="bold"
                              color="text.secondary"
                              sx={{ letterSpacing: 1,
                                display: 'block', mb: 0.5 }}>
                              NYCKELORD
                            </Typography>
                            <Box sx={{ display: 'flex',
                              flexWrap: 'wrap', gap: 0.5 }}>
                              {project.keywords.map((k, i) => (
                                <Chip key={i} label={k} size="small"
                                  sx={{ bgcolor: 'action.hover',
                                    fontSize: '0.7rem' }} />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {project.standards?.length > 0 && (
                          <Box>
                            <Typography variant="caption"
                              fontWeight="bold"
                              color="text.secondary"
                              sx={{ letterSpacing: 1,
                                display: 'block', mb: 0.5 }}>
                              STANDARDER
                            </Typography>
                            <Box sx={{ display: 'flex',
                              flexWrap: 'wrap', gap: 0.5 }}>
                              {project.standards.map((s, i) => (
                                <Chip key={i} label={s} size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }} />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {project.software?.length > 0 && (
                          <Box>
                            <Typography variant="caption"
                              fontWeight="bold"
                              color="text.secondary"
                              sx={{ letterSpacing: 1,
                                display: 'block', mb: 0.5 }}>
                              PROGRAMVAROR
                            </Typography>
                            <Box sx={{ display: 'flex',
                              flexWrap: 'wrap', gap: 0.5 }}>
                              {project.software.map((s, i) => (
                                <Chip key={i} label={s} size="small"
                                  sx={{ bgcolor: '#e3f2fd',
                                    color: '#1565c0',
                                    fontSize: '0.7rem' }} />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>

                      {project.sourceFiles?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption"
                            fontWeight="bold" color="text.secondary"
                            sx={{ letterSpacing: 1 }}>
                            KÄLLDOKUMENT
                          </Typography>
                          <List dense sx={{ mt: 0.5 }}>
                            {project.sourceFiles.map((file, i) => (
                              <ListItem key={i}
                                sx={{ py: 0.3, px: 0 }}>
                                <InsertDriveFileIcon
                                  fontSize="small"
                                  sx={{ mr: 1,
                                    color: 'text.secondary',
                                    fontSize: 16 }} />
                                <ListItemText primary={
                                  <Typography variant="caption"
                                    component="a" href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ color: 'primary.main',
                                      textDecoration: 'none',
                                      '&:hover': {
                                        textDecoration: 'underline'
                                      } }}>
                                    {file.name}
                                  </Typography>
                                } />
                                <ListItemSecondaryAction>
                                  <IconButton size="small" edge="end"
                                    onClick={() =>
                                      handleDeleteSourceFile(
                                        project.id, file.url)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Grid>

                    {/* HÖGER – Projektbild */}
                    {project.image && (
                      <Grid item xs={12} md={4}>
                        <Box component="img" src={project.image}
                          sx={{ width: '100%', borderRadius: 2,
                            objectFit: 'cover', maxHeight: 360,
                            border: '1px solid',
                            borderColor: 'divider',
                            display: 'block' }} />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Collapse>
            </Paper>
          );
        });
        })()}
      </Container>

      {/* DIALOG – 2-stegs */}
      <Dialog open={dialog.open} onClose={handleCloseDialog}
        maxWidth="md" fullWidth>
        <DialogTitle>
          <Box>
            {dialog.editId
              ? 'Redigera referensuppdrag'
              : 'Nytt referensuppdrag'}
            {!dialog.editId && (
              <Stepper activeStep={dialogStep} sx={{ mt: 1.5 }}>
                <Step>
                  <StepLabel>Ladda upp dokument</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Granska och spara</StepLabel>
                </Step>
              </Stepper>
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers>

          {/* STEG 0 */}
          {dialogStep === 0 && (
            <Box sx={{ py: 2 }}>
              <Box sx={{
                p: 4, bgcolor: '#fff8e1',
                border: '2px dashed #FF6B00', borderRadius: 2,
                textAlign: 'center', mb: 3
              }}>
                <AutoFixHighIcon sx={{ color: '#FF6B00',
                  fontSize: 48, mb: 1.5 }} />
                <Typography variant="h6" fontWeight="bold"
                  gutterBottom>
                  Ladda upp referensblad
                </Typography>
                <Typography variant="body2" color="text.secondary"
                  gutterBottom>
                  AI läser dokumentet och fyller i alla fält automatiskt.
                </Typography>

                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="caption"
                    color="text.secondary" gutterBottom
                    display="block">
                    Detaljnivå på AI-analysen:
                  </Typography>
                  <ToggleButtonGroup value={detailLevel} exclusive
                    size="small"
                    onChange={(e, val) => {
                      if (val) setDetailLevel(val);
                    }}>
                    <ToggleButton value="short">
                      Kort (S)
                    </ToggleButton>
                    <ToggleButton value="medium">
                      Medel (M)
                    </ToggleButton>
                    <ToggleButton value="long">
                      Utforlig (L)
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Typography variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}>
                    {detailLevel === 'short' &&
                      'Kortfattade beskrivningar, max 5 uppgifter'}
                    {detailLevel === 'medium' &&
                      'Lagom detaljerat, de viktigaste delarna'}
                    {detailLevel === 'long' &&
                      'All information från dokumentet inkluderas'}
                  </Typography>
                </Box>

                <Button variant="contained" component="label"
                  size="large" startIcon={<UploadFileIcon />}
                  sx={{ bgcolor: '#FF6B00', color: 'white',
                    '&:hover': { bgcolor: '#e65c00' } }}>
                  Välj PDF eller Word-fil
                  <input type="file" hidden accept=".pdf,.docx"
                    multiple onChange={e =>
                      setPendingFiles(Array.from(e.target.files))} />
                </Button>

                {pendingFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {pendingFiles.map((f, i) => (
                      <Box key={i} sx={{ display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1, mt: 0.5 }}>
                        <InsertDriveFileIcon sx={{ fontSize: 16,
                          color: '#FF6B00' }} />
                        <Typography variant="body2"
                          sx={{ color: '#FF6B00',
                            fontWeight: 'bold' }}>
                          {f.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Vill du fylla i manuellt utan AI?
                </Typography>
                <Button size="small" sx={{ mt: 0.5 }}
                  onClick={() => setDialogStep(1)}>
                  Hoppa över – fyll i manuellt
                </Button>
              </Box>
            </Box>
          )}

          {/* STEG 1 */}
          {dialogStep === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column',
              gap: 2.5 }}>

              <Box>
                <Typography variant="subtitle2" fontWeight="bold"
                  gutterBottom>
                  Projektbild
                </Typography>
                <ImageUpload
                  currentUrl={form.image}
                  onUpload={url => setForm(prev => ({
                    ...prev, image: url
                  }))}
                  path={'referenceProjects/images'}
                  label="Ladda upp projektbild"
                />
              </Box>

              <Divider />

              <Typography variant="subtitle2" fontWeight="bold">
                Grundinformation
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <TextField label="Uppdragsnummer"
                    value={form.projectNumber}
                    fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, projectNumber: e.target.value }))} />
                </Grid>
                <Grid item xs={10}>
                  <TextField label="Uppdragsnamn *"
                    value={form.name} fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, name: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Roll i uppdraget"
                    value={form.role} fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, role: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Konsultens namn"
                    value={form.consultantName}
                    fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, consultantName: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Projekttyp"
                    value={form.projectType} fullWidth size="small"
                    placeholder="t.ex. Renovering, Infrastruktur"
                    onChange={e => setForm(p => ({
                      ...p, projectType: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Affärsområde" select
                    value={form.departmentId}
                    fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, departmentId: e.target.value }))}
                    SelectProps={{ native: true }}>
                    <option value="">Välj affärsområde</option>
                    {departments.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Divider />

              <Typography variant="subtitle2" fontWeight="bold">
                Referensperson hos beställare
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField label="Uppdragsgivare"
                    value={form.client} fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, client: e.target.value }))} />
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Org.nr"
                    value={form.clientOrgNumber}
                    fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, clientOrgNumber: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Referensperson namn"
                    value={form.contactName} fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, contactName: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Befattning"
                    value={form.contactRole} fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, contactRole: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="E-post"
                    value={form.contactEmail} fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, contactEmail: e.target.value }))} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Telefon"
                    value={form.contactPhone} fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, contactPhone: e.target.value }))} />
                </Grid>
              </Grid>

              <Divider />

              <Typography variant="subtitle2" fontWeight="bold">
                Uppgifter kring uppdrag
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField label="Budget" value={form.budget}
                    fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, budget: e.target.value }))} />
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Uppdragstart"
                    value={form.startDate} fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, startDate: e.target.value }))} />
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Uppdragslut"
                    value={form.endDate} fullWidth size="small"
                    onChange={e => setForm(p => ({
                      ...p, endDate: e.target.value }))} />
                </Grid>
              </Grid>

              <Divider />

              <Typography variant="subtitle2" fontWeight="bold">
                Beskrivning
              </Typography>
              <TextField label="Projektbeskrivning"
                value={form.projectDescription}
                fullWidth multiline rows={4} size="small"
                helperText="Beskrivning av projektet i stort"
                onChange={e => setForm(p => ({
                  ...p, projectDescription: e.target.value }))} />
              <TextField label="Vart uppdrag"
                value={form.ourAssignment}
                fullWidth multiline rows={3} size="small"
                helperText="Vart specifika uppdrag och ansvar"
                onChange={e => setForm(p => ({
                  ...p, ourAssignment: e.target.value }))} />

              <Box>
                <Typography variant="caption" fontWeight="bold"
                  color="text.secondary" sx={{ letterSpacing: 1 }}>
                  UPPGIFTSLISTA
                </Typography>
                {form.taskList.map((task, i) => (
                  <Box key={i} sx={{ display: 'flex',
                    alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      • {task}
                    </Typography>
                    <IconButton size="small"
                      onClick={() => setForm(p => ({
                        ...p, taskList: p.taskList.filter(
                          (_, idx) => idx !== i)
                      }))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField size="small" fullWidth
                    value={taskInput}
                    placeholder="Lägg till uppgift..."
                    onChange={e => setTaskInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && taskInput.trim()) {
                        setForm(p => ({
                          ...p, taskList: [
                            ...p.taskList, taskInput.trim()
                          ]
                        }));
                        setTaskInput('');
                      }
                    }}
                  />
                  <IconButton size="small"
                    disabled={!taskInput.trim()}
                    onClick={() => {
                      setForm(p => ({
                        ...p, taskList: [
                          ...p.taskList, taskInput.trim()
                        ]
                      }));
                      setTaskInput('');
                    }}
                    sx={{ border: '1px solid',
                      borderColor: 'divider' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" fontWeight="bold"
                  color="text.secondary" sx={{ letterSpacing: 1 }}>
                  NYCKELORD
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap',
                  gap: 0.5, mt: 0.5 }}>
                  {form.keywords.map((k, i) => (
                    <Chip key={i} label={k} size="small"
                      onDelete={() => setForm(p => ({
                        ...p, keywords: p.keywords.filter(
                          (_, idx) => idx !== i)
                      }))} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField size="small" fullWidth
                    value={keywordInput}
                    placeholder="Lägg till nyckelord..."
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && keywordInput.trim()) {
                        setForm(p => ({
                          ...p, keywords: [
                            ...p.keywords, keywordInput.trim()
                          ]
                        }));
                        setKeywordInput('');
                      }
                    }}
                  />
                  <IconButton size="small"
                    disabled={!keywordInput.trim()}
                    onClick={() => {
                      setForm(p => ({
                        ...p, keywords: [
                          ...p.keywords, keywordInput.trim()
                        ]
                      }));
                      setKeywordInput('');
                    }}
                    sx={{ border: '1px solid',
                      borderColor: 'divider' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" fontWeight="bold"
                  color="text.secondary" sx={{ letterSpacing: 1 }}>
                  STANDARDER
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap',
                  gap: 0.5, mt: 0.5 }}>
                  {form.standards.map((s, i) => (
                    <Chip key={i} label={s} size="small"
                      variant="outlined"
                      onDelete={() => setForm(p => ({
                        ...p, standards: p.standards.filter(
                          (_, idx) => idx !== i)
                      }))} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField size="small" fullWidth
                    value={standardInput}
                    placeholder="Lägg till standard..."
                    onChange={e => setStandardInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && standardInput.trim()) {
                        setForm(p => ({
                          ...p, standards: [
                            ...p.standards, standardInput.trim()
                          ]
                        }));
                        setStandardInput('');
                      }
                    }}
                  />
                  <IconButton size="small"
                    disabled={!standardInput.trim()}
                    onClick={() => {
                      setForm(p => ({
                        ...p, standards: [
                          ...p.standards, standardInput.trim()
                        ]
                      }));
                      setStandardInput('');
                    }}
                    sx={{ border: '1px solid',
                      borderColor: 'divider' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" fontWeight="bold"
                  color="text.secondary" sx={{ letterSpacing: 1 }}>
                  PROGRAMVAROR
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <SoftwareTagInput
                    value={form.software}
                    onChange={arr => setForm(p => ({ ...p, software: arr }))}
                    pool={softwarePool}
                    onAddNew={addSoftware}
                    label="Programvara"
                    placeholder="Sök eller lägg till programvara..."
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>Avbryt</Button>
          {dialogStep === 0 && (
            <Button variant="contained"
              startIcon={importing
                ? <CircularProgress size={16} color="inherit" />
                : <AutoFixHighIcon />}
              onClick={handleUploadAndAnalyze}
              disabled={importing}
              sx={{ bgcolor: '#FF6B00', color: 'white',
                '&:hover': { bgcolor: '#e65c00' } }}>
              {importing ? 'Analyserar...' :
                pendingFiles.length > 0
                  ? 'Ladda upp och AI-analysera'
                  : 'Nästa – fyll i manuellt'}
            </Button>
          )}
          {dialogStep === 1 && (
            <>
              {!dialog.editId && (
                <Button onClick={() => setDialogStep(0)}>
                  Tillbaka
                </Button>
              )}
              <Button variant="contained" onClick={handleSave}
                disabled={!form.name.trim()}>
                Spara
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}